import { formatCost } from "@ccflare/core";
import { Area, AreaChart } from "recharts";
import { useChartColors } from "../../hooks/useChartColors";
import { formatCompactCurrency } from "../../lib/chart-utils";
import { BaseXYChartFrame } from "./BaseXYChartFrame";

interface CostChartProps {
	data: Array<{
		time: string;
		cost: number;
		theoreticalCost: number;
		[key: string]: string | number;
	}>;
	loading?: boolean;
	height?: number;
	// Accepted for parity with other charts; not used here.
	viewMode?: "normal" | "cumulative";
	timeRange?: string;
}

/**
 * Two-series cost chart: "Theoretical" (list-price equivalent for all tokens)
 * and "Cost" (money actually drawn — 0 while within plan limits).
 */
export function CostChart({
	data,
	loading = false,
	height = 400,
}: CostChartProps) {
	const colors = useChartColors();
	return (
		<BaseXYChartFrame
			chartComponent={AreaChart}
			data={data}
			loading={loading}
			height={height}
			xAxisKey="time"
			yAxisTickFormatter={formatCompactCurrency}
			tooltipFormatter={(value, name) => [formatCost(Number(value)), name]}
			showLegend
			defs={
				<>
					<linearGradient id="colorCostTheoretical" x1="0" y1="0" x2="0" y2="1">
						<stop offset="0%" stopColor="var(--warning)" stopOpacity={0.5} />
						<stop offset="100%" stopColor="var(--warning)" stopOpacity={0.05} />
					</linearGradient>
					<linearGradient id="colorCostPaid" x1="0" y1="0" x2="0" y2="1">
						<stop offset="0%" stopColor={colors.primary} stopOpacity={0.7} />
						<stop offset="100%" stopColor={colors.primary} stopOpacity={0.05} />
					</linearGradient>
				</>
			}
		>
			<Area
				type="monotone"
				name="Theoretical"
				dataKey="theoreticalCost"
				stroke="var(--warning)"
				strokeWidth={2}
				fill="url(#colorCostTheoretical)"
			/>
			<Area
				type="monotone"
				name="Cost"
				dataKey="cost"
				stroke={colors.primary}
				strokeWidth={2}
				fill="url(#colorCostPaid)"
			/>
		</BaseXYChartFrame>
	);
}
